import { Injectable } from '@angular/core'
import { GridApi } from '@ag-grid-community/core'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, map, startWith } from 'rxjs/operators'
import {
  DEFAULT_SOURCE,
  FALLBACK_SOURCE,
  GridRole,
  gridKeyOf,
  MatchTargetKey,
  otherRole,
  RowOrigin,
  SourceKey,
} from '../models/source-key.model'
import { isSourceSelectable } from '../registry/source-registry'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from './matching.service'

type PerRole<T> = Record<GridRole, T>

const emptySelection = (): PerRole<Set<string>> => ({
  comparison: new Set<string>(),
  base: new Set<string>(),
})

const noCrossFilter = (): PerRole<string[] | null> => ({
  comparison: null,
  base: null,
})

const setsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every(v => b.has(v))

/**
 * A new match group must span at least two distinct grid sources — the client-side half of the
 * Match gate (see {@link WorkbenchStoreService.selectionSpansSources}). Same threshold the backend
 * re-validates on submit.
 */
const MIN_MATCH_SOURCES = 2

/**
 * The three-state resolution of "the single position driving a sub-panel", as the design prototype
 * defines it (`libs/panels/positions-2/docs/Positions 2.0.dc.html:1811-1815`, re-derived inside
 * `detailFor()` at `:2266-2275`).
 *
 * A discriminated union rather than a row plus a count, because the three outcomes drive three
 * different renderings and only one of them has a row: `single` shows content, `multiple` shows the
 * "Multiple Positions Selected" card, `none` shows "No Position Selected". Returning
 * `WorkbenchRow | null` plus a separate count would let a caller read a stale count against a
 * fresh row, which is exactly the bug this shape makes unrepresentable.
 */
export type AnchorSelection =
  | { kind: 'none' }
  | { kind: 'single'; role: GridRole; row: WorkbenchRow }
  | { kind: 'multiple' }

/**
 * Equality for {@link AnchorSelection}, since every resolution allocates a fresh wrapper object and
 * default reference equality would therefore never de-dupe anything.
 *
 * `single` compares the row by REFERENCE, not by rowId — same rationale as {@link
 * WorkbenchStoreService.anchorRow}: a refetch of the anchor's own source hands back a new
 * WorkbenchRow object for the same id, and a subscriber reading `fields` off it must see the new
 * object rather than being stranded on the pre-refetch one.
 */
const sameAnchorSelection = (
  a: AnchorSelection,
  b: AnchorSelection,
): boolean => {
  if (a.kind !== b.kind) return false
  if (a.kind === 'single' && b.kind === 'single') {
    return a.role === b.role && a.row === b.row
  }
  return true
}

/**
 * Panel-scoped mediator for the two grids. Owns source-per-role assignment, selection sync,
 * cross-filter targets and the shared row set. The grids talk only to this service, never to each
 * other. Provided at the host component so there is exactly one instance per panel.
 */
@Injectable()
export class WorkbenchStoreService {
  /**
   * Rows per source, published by whichever grid is showing that source once its catalog resolves
   * and its page comes back. A source no grid is pointed at has no rows loaded, so match counts
   * against it read zero until it is displayed.
   */
  private readonly rowsBySource = new Map<SourceKey, WorkbenchRow[]>()

  private readonly rowsSubject = new Subject<void>()

  private readonly rowsPatchedSubject = new Subject<Set<string>>()

  private readonly sourceSubject = new BehaviorSubject<PerRole<SourceKey>>({
    comparison: DEFAULT_SOURCE.comparison,
    base: DEFAULT_SOURCE.base,
  })
  private readonly selectionSubject = new BehaviorSubject<PerRole<Set<string>>>(
    emptySelection(),
  )
  private readonly crossFilterSubject = new BehaviorSubject<
    PerRole<string[] | null>
  >(noCrossFilter())

  /**
   * The focused account id, shared across both grids. The field-catalog API 400s without one, so
   * the column pipeline skips its fetch while this is null.
   */
  private readonly accountIdSubject = new BehaviorSubject<number | null>(null)

  private readonly apis: Partial<PerRole<GridApi>> = {}

  /** Whichever role most recently gained a (non-empty) selection — see {@link anchorRow}. */
  private lastSelectionRole: GridRole | null = null

  /** Fires whenever any source's rows are replaced, so both grids can re-read what they need. */
  readonly rowsChanged$: Observable<void> = this.rowsSubject.asObservable()

  /**
   * Row ids whose match columns were just patched in place by {@link patchMatchColumns}. The grids
   * ride this into the same refresh they already do for `matching.changes$` — the row OBJECTS are
   * unchanged by reference, so nothing repaints without it.
   */
  readonly rowsPatched$: Observable<Set<string>> =
    this.rowsPatchedSubject.asObservable()

  constructor(private readonly matching: MatchingService) {}

  // ---- source assignment ----------------------------------------------------

  sourceValue(role: GridRole): SourceKey {
    return this.sourceSubject.value[role]
  }

  sourceFor(role: GridRole): Observable<SourceKey> {
    return this.sourceSubject.pipe(
      map(sources => sources[role]),
      distinctUntilChanged(),
    )
  }

  /**
   * Point a grid at a source, honouring the allowed roles and the Enfusion mutual-exclusion rule:
   * Enfusion may only occupy one grid, so selecting it where it is already the other grid's source
   * swaps the two. Selection + cross-filters reset on any change.
   */
  setSource(role: GridRole, key: SourceKey): void {
    // Covers both "not allowed for this role" and "held back for now". The picker disables the
    // latter, but guarding here too means no other path can strand a grid on it.
    if (!isSourceSelectable(role, key)) return
    const current = this.sourceSubject.value
    if (current[role] === key) return

    const next: PerRole<SourceKey> = { ...current, [role]: key }
    if (key === 'api') {
      const other = otherRole(role)
      if (next[other] === 'api') next[other] = FALLBACK_SOURCE[other]
    }
    this.sourceSubject.next(next)
    this.selectionSubject.next(emptySelection())
    this.crossFilterSubject.next(noCrossFilter())
  }

  // ---- rows -----------------------------------------------------------------

  rowsForSource(key: SourceKey): WorkbenchRow[] {
    return this.rowsBySource.get(key) ?? []
  }

  /**
   * Replace one source's rows and rebuild the matching index over everything currently loaded.
   * The index has to span sources: a match is a relationship between rows from different feeds.
   */
  setRowsForSource(key: SourceKey, rows: WorkbenchRow[]): void {
    this.rowsBySource.set(key, rows)
    this.matching.init(this.loadedRows())
    this.rowsSubject.next()
  }

  private loadedRows(): WorkbenchRow[] {
    const rows: WorkbenchRow[] = []
    this.rowsBySource.forEach(sourceRows => rows.push(...sourceRows))
    return rows
  }

  /** Every loaded row carrying one of the given ids, across all sources. */
  rowsByIds(rowIds: string[]): WorkbenchRow[] {
    const wanted = new Set(rowIds)
    return this.loadedRows().filter(row => wanted.has(row.rowId))
  }

  /**
   * Fold a freshly re-read row's match columns onto the rendered row objects that already carry its
   * identity, and announce which ids moved.
   *
   * Deliberately NOT {@link setRowsForSource}: that replaces the array and rebuilds the index, but
   * no grid subscribes to `rowsChanged$` — the grids only call `setRowData` when a catalog resolves,
   * so their `rowData` would keep the old objects and go on rendering stale cells. Mutating the
   * objects the grid, this store and {@link MatchingService} all share by reference is the only
   * mechanism that actually repaints, which is why {@link rowsPatched$} exists to trigger the
   * refresh the grid already performs for `matching.changes$`.
   *
   * Identity is `(rowId, accountId)`, never `rowId` alone: `by-ids` scopes by
   * `(sessionId, recordSource, rowId IN …)` with no account, and a position id is not unique across
   * accounts, so one requested id can come back as several rows — matching on the id alone would
   * write another account's match state onto the rendered row.
   *
   * Only the three server match fields and `availableActions` are touched. `group` is never set (a
   * group would make the row read as locally matched forever), and nothing else on the row is
   * replaced, so a patch for one row cannot disturb another's cells or the anchor's reference
   * identity.
   */
  patchMatchColumns(key: SourceKey, patched: WorkbenchRow[]): void {
    if (!patched.length) return

    const rendered = this.rowsForSource(key)
    if (!rendered.length) return

    const patchedIds = new Set<string>()
    patched.forEach(patch => {
      rendered
        .filter(
          row => row.rowId === patch.rowId && row.accountId === patch.accountId,
        )
        .forEach(row => {
          row.serverMatchStatus = patch.serverMatchStatus
          row.serverMatchCounts = patch.serverMatchCounts
          row.serverMatchedIds = patch.serverMatchedIds
          // Eligibility moves WITH match state — a row that just became matched gains Unmatch and
          // loses Match — so re-reading the match fields and leaving this one behind would freeze
          // the context menu on the pre-action verdict, which is exactly when it is wrong. Copied
          // unconditionally rather than only-when-present: `by-ids` projects nothing and so returns
          // a superset of the query's fields, meaning an absent value here is a real "no
          // restriction", not a gap in the response.
          row.availableActions = patch.availableActions
          patchedIds.add(row.rowId)
        })
    })

    // An announcement for nothing would redraw both grids for no reason — an event naming only
    // unknown ids, or ids belonging to another account, has to stay a true no-op.
    if (!patchedIds.size) return
    this.rowsPatchedSubject.next(patchedIds)
  }

  // ---- account --------------------------------------------------------------

  /** The focused account id, or null when no account is loaded (the field-catalog fetch is skipped). */
  accountIdValue(): number | null {
    return this.accountIdSubject.value
  }

  /** Emits the focused account id; the column pipeline refetches per account. */
  get accountId$(): Observable<number | null> {
    return this.accountIdSubject.pipe(distinctUntilChanged())
  }

  /** Focus an account. No-op when unchanged. */
  setAccountId(accountId: number | null): void {
    if (this.accountIdSubject.value === accountId) return
    this.accountIdSubject.next(accountId)
  }

  // ---- selection + related-row highlighting ---------------------------------

  registerGridApi(role: GridRole, api: GridApi): void {
    this.apis[role] = api
  }

  gridApi(role: GridRole): GridApi | undefined {
    return this.apis[role]
  }

  reportSelection(role: GridRole, rowIds: string[]): void {
    const current = this.selectionSubject.value
    const next = new Set(rowIds)
    // A fresh (non-empty) selection becomes the preferred anchor source, mirroring Positions 1.0's
    // "a fresh click replaces the prior selection" rule — extended across positions-2's two grids.
    if (rowIds.length > 0) this.lastSelectionRole = role
    if (setsEqual(current[role], next)) return
    this.selectionSubject.next({ ...current, [role]: next })
  }

  selectionFor(role: GridRole): Observable<Set<string>> {
    return this.selectionSubject.pipe(
      map(selection => selection[role]),
      distinctUntilChanged(setsEqual),
    )
  }

  /**
   * The single row driving the detail sub-panels, across both grids. When both hold a selection at
   * once (e.g. one row picked in each to set up a Match), the most recently selected one wins,
   * falling back to whichever role still holds a selection if the preferred one has since been
   * cleared, and to null once neither does. Re-resolves whenever the rows themselves change too,
   * since a refetch replaces the row objects the selected ids point at.
   */
  anchorRow(): Observable<WorkbenchRow | null> {
    return combineLatest([
      this.selectionSubject,
      this.rowsSubject.pipe(startWith(undefined)),
    ]).pipe(
      map(([selection]) => this.pickAnchor(selection)),
      // Default (reference) equality, not a rowId comparator: pickAnchor returns the row object
      // straight out of rowsBySource, so an unrelated rows change still resolves to the SAME
      // object (filtered out here, correctly) while a refetch of the anchor's own source replaces
      // it with a NEW object for the same rowId — comparing by rowId alone would wrongly treat
      // that as unchanged and strand subscribers on the pre-refetch object.
      distinctUntilChanged(),
    )
  }

  private pickAnchor(selection: PerRole<Set<string>>): WorkbenchRow | null {
    const preference: GridRole[] = this.lastSelectionRole
      ? [this.lastSelectionRole, otherRole(this.lastSelectionRole)]
      : ['comparison', 'base']
    const role = preference.find(r => selection[r].size > 0)
    if (!role) return null
    const [id] = selection[role]
    return this.loadedRows().find(row => row.rowId === id) ?? null
  }

  /**
   * The single position driving the Position Detail sub-panel (and, once they are built, Historical
   * Balance and a migrated Position Comparison), resolved by the design prototype's rule rather
   * than by {@link anchorRow}'s.
   *
   * The rule, from `docs/Positions 2.0.dc.html:1811-1815`: scan the two roles last-interacted
   * FIRST, and the first role holding EXACTLY ONE selected id whose row is actually loaded wins.
   * Otherwise the answer is `multiple` when some role holds more than one id, and `none` when
   * neither does.
   *
   * Two consequences look like bugs and are not:
   *  - A role holding an id whose row is NOT loaded does not win, and does not stop the scan — it
   *    falls through to the other role, mirroring the prototype's `if(r)` continue at `:1813`.
   *    (`detailFor()`'s re-derivation at `:2269` breaks instead of continuing; `:1813` is the
   *    authority, and continuing is the strictly more useful of the two.)
   *  - `base: 3 rows + comparison: 1 row` resolves to `single`/comparison, so the user sees detail
   *    for a row in the grid they are *not* mostly working in. That falls straight out of "first
   *    role with exactly one id wins" — see the case table in this service's spec.
   *
   * Deliberately NOT built on {@link pickAnchor}: that method resolves "most recently selected row
   * wins" with no cardinality gate at all, which is the live Position Comparison tab's contract and
   * must not change. This is an additive second accessor, not a replacement.
   *
   * TODO (follow-up F2 — requested, not yet filed): Position Comparison still uses {@link
   * anchorRow}, so the two sub-panels can disagree about which row is the anchor when both grids
   * hold a selection. Migrating Comparison (and the future Historical Balance tab) onto this
   * accessor is a behaviour change to a shipped tab and needs its own ticket. The request is
   * drafted in full — defect, locus, blast radius — under "Requested follow-ups" in
   * libs/panels/positions-2/README.md; swap the F2 label here for the issue key once it is filed.
   * Deliberately NOT tagged with PVAL-26293: this ticket is the one that introduced the accessor,
   * so pointing the follow-up back at it (or at this branch's merge request) leaves a dead pointer
   * the moment either is closed.
   */
  anchorSelection(): Observable<AnchorSelection> {
    return combineLatest([
      this.selectionSubject,
      this.rowsSubject.pipe(startWith(undefined)),
    ]).pipe(
      map(([selection]) => this.resolveAnchorSelection(selection)),
      distinctUntilChanged(sameAnchorSelection),
    )
  }

  /** Synchronous read of {@link anchorSelection}'s current value. */
  anchorSelectionValue(): AnchorSelection {
    return this.resolveAnchorSelection(this.selectionSubject.value)
  }

  private resolveAnchorSelection(
    selection: PerRole<Set<string>>,
  ): AnchorSelection {
    // Last-interacted role first (prototype `:1812`). The prototype's null branch defaults
    // base-first while pickAnchor defaults comparison-first; one convention per file wins here
    // because the branch is unreachable while any selection exists — `lastSelectionRole` is only
    // ever SET (reportSelection, above, never nulls it) and the only other selectionSubject
    // writers are full clears, so null implies both roles empty, which both orderings resolve to
    // `none` regardless.
    const order: GridRole[] = this.lastSelectionRole
      ? [this.lastSelectionRole, otherRole(this.lastSelectionRole)]
      : ['comparison', 'base']

    for (const role of order) {
      if (selection[role].size !== 1) continue
      const [id] = selection[role]
      const row = this.loadedRows().find(r => r.rowId === id)
      // `if(r)` at `:1813`: an id whose row has not loaded yet is not an anchor, and does not stop
      // the scan either.
      if (row) return { kind: 'single', role, row }
    }

    return order.some(role => selection[role].size > 1)
      ? { kind: 'multiple' }
      : { kind: 'none' }
  }

  /** Union of the row ids selected across both grids — the scope of a Match action. */
  allSelectedIds(): string[] {
    const selection = this.selectionSubject.value
    return [...selection.comparison, ...selection.base]
  }

  /** Row ids selected in one grid. */
  selectedIds(role: GridRole): string[] {
    return [...this.selectionSubject.value[role]]
  }

  /**
   * The loaded ROWS behind {@link allSelectedIds} — the scope a Match dispatches over.
   *
   * Rows, not ids, because the actions endpoint splits its request by feed (`enfnPosIds` vs
   * `promPosIds`) and only the row carries the origin that decides which bucket it lands in.
   *
   * Ids whose row is not loaded are skipped, the same way {@link selectedGridKeys} skips them: a
   * selection can outlive its rows across a refetch, and an id with no row has no origin to bucket
   * on. Ordered by load order rather than by selection order — the endpoint takes sets.
   */
  allSelectedRows(): WorkbenchRow[] {
    const ids = new Set(this.allSelectedIds())
    return this.loadedRows().filter(row => ids.has(row.rowId))
  }

  /**
   * True when both grids have a selection.
   *
   * NO LONGER THE MATCH GATE — {@link selectionSpansSources} is (PVAL-26403). This asks the weaker
   * question, and it both over- and under-approximates the real one: it says yes to one Enfusion row
   * selected in each grid (one source, so not matchable), and no to a two-source selection made
   * inside a single grid (matchable). Kept because it is the accessor transactions-2's grid still
   * gates its own Match on, so deleting it here would silently diverge the twin panels' stores; it
   * is also the contrast case the doc-comment below and the panel README both point at. Nothing in
   * positions-2's production code reads it today.
   */
  bothGridsHaveSelection(): boolean {
    const selection = this.selectionSubject.value
    return selection.comparison.size > 0 && selection.base.size > 0
  }

  /**
   * The distinct GRID SOURCES the live cross-grid selection covers, folded through
   * {@link gridKeyOf} so a Custody row and a Trading row both count as `prom`.
   *
   * Ids whose row is not loaded are skipped — a selection can outlive its rows (a refetch replaces
   * them), and an id we cannot resolve has no origin to fold.
   */
  selectedGridKeys(): SourceKey[] {
    const ids = new Set(this.allSelectedIds())
    const keys = new Set<SourceKey>()
    this.loadedRows().forEach(row => {
      if (ids.has(row.rowId)) keys.add(gridKeyOf(row.origin))
    })
    return [...keys]
  }

  /**
   * The UI-only half of the Match gate (PVAL-26403): a new match group needs the selection to span
   * at least two distinct grid sources, and only the client knows the live selection.
   *
   * Counted by GRID SOURCE, not by origin, which is the whole point of routing through
   * {@link gridKeyOf}: a Custody row plus a Trading row is one source (Prometheus) matched against
   * itself and does NOT qualify, even though those are two distinct origins. Contrast
   * {@link bothGridsHaveSelection}, which asks the weaker question "is something selected in each
   * grid" — that one cannot see two sources selected inside a single grid, nor two rows of the same
   * source across both grids, so it is not the gate this action needs.
   *
   * Not the security boundary: the `/flex-positions/actions` endpoint and the worker's
   * CreateMatchGroupCommand re-check >= 2 distinct sources on submit.
   */
  selectionSpansSources(): boolean {
    return this.selectedGridKeys().length >= MIN_MATCH_SOURCES
  }

  /**
   * The distinct row ORIGINS selected in the OTHER grid.
   *
   * Origins, not grid sources: the Match Additional rule compares them against the origins of an
   * existing group's members, and Custody and Trading are separate members there even though both
   * fold to `prom` as a grid source. Contrast {@link selectedGridKeys}, which deliberately folds.
   *
   * Ids whose row is not loaded are skipped, for the same reason as `selectedGridKeys`: a selection
   * can outlive its rows across a refetch.
   *
   * @param role the grid asking (it gets its sibling's selected origins)
   */
  selectedOriginsForOther(role: GridRole): Set<RowOrigin> {
    const ids = this.selectionSubject.value[otherRole(role)]
    const origins = new Set<RowOrigin>()
    this.loadedRows().forEach(row => {
      if (ids.has(row.rowId)) origins.add(row.origin)
    })
    return origins
  }

  /** Groups selected in the OTHER grid — used to tint related rows in this grid. */
  relatedGroupsForOther(role: GridRole): Observable<Set<string>> {
    const other = otherRole(role)
    return this.selectionSubject.pipe(
      map(selection => this.groupsOfSelection(selection[other])),
      distinctUntilChanged(setsEqual),
    )
  }

  private groupsOfSelection(ids: Set<string>): Set<string> {
    const groups = new Set<string>()
    this.loadedRows().forEach(row => {
      if (ids.has(row.rowId) && row.group) groups.add(row.group)
    })
    return groups
  }

  // ---- cross-filter ---------------------------------------------------------

  crossFilterFor(role: GridRole): Observable<string[] | null> {
    return this.crossFilterSubject.pipe(
      map(cf => cf[role]),
      distinctUntilChanged(),
    )
  }

  /**
   * Cross-filter the OTHER grid down to the matched rows, auto-switching that grid's source to the
   * one that can show them (Custody/Trading -> Prometheus, etc.).
   */
  crossFilterToMatches(
    fromRole: GridRole,
    target: MatchTargetKey,
    rowIds: string[],
  ): void {
    if (!rowIds.length) return
    const required = gridKeyOf(target)
    const other = otherRole(fromRole)
    const current = this.sourceSubject.value

    // The matched rows can only be shown if the other grid may actually display their source. A
    // cross-filter must never be what puts a grid onto a source its picker refuses.
    if (current[other] !== required && !isSourceSelectable(other, required)) {
      return
    }

    if (current[other] !== required) {
      const next: PerRole<SourceKey> = { ...current, [other]: required }
      if (required === 'api' && next[fromRole] === 'api') {
        next[fromRole] = FALLBACK_SOURCE[fromRole]
      }
      this.sourceSubject.next(next)
    }
    this.selectionSubject.next(emptySelection())
    this.crossFilterSubject.next({ ...noCrossFilter(), [other]: rowIds })
  }

  clearCrossFilter(role: GridRole): void {
    const current = this.crossFilterSubject.value
    if (current[role] == null) return
    this.crossFilterSubject.next({ ...current, [role]: null })
  }
}
